import React, { useState, useEffect, useRef } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {
  fetchPincodeSuggestions,
  getPincodeMasterRecord,
} from "../../api/formService";
import {
  isNameLikeSemanticField,
  NAME_FIELD_MAX_CHARS,
  isDateOfBirthField,
} from "../utils/dynamicValidation";

const EMAIL_DOMAINS = [
  "gmail.com",
  "yahoo.com",
  "outlook.com",
  "hotmail.com",
];

/**
 * Render form label with required indicator
 */
const FormLabel = ({ id, label, required }) => {
  if (!label) return null;
  return (
    <label className="label-one m-0" htmlFor={id}>
      {label}
      {required && <span style={{ color: "#dc2626" }}> *</span>}
    </label>
  );
};

/**
 * Render error message with accessible announcement
 */
const ErrorMessage = ({ error, id }) => {
  if (!error) return null;
  return (
    <div className="error-text" id={id} role="alert" aria-live="assertive">
      {error}
    </div>
  );
};

/**
 * Render form row wrapper
 */
const FormRow = ({ label, labelId, required, error, children }) => {
  const errorId = error && labelId ? `${labelId}-error` : undefined;
  return (
    <div className="form-row">
      <FormLabel id={labelId} label={label} required={required} />
      {/* Inject aria-invalid and aria-describedby into the first form control child */}
      {React.Children.map(children, (child) => {
        if (
          React.isValidElement(child) &&
          (child.type === "input" ||
            child.type === "select" ||
            child.type === "textarea")
        ) {
          return React.cloneElement(child, {
            "aria-invalid": error ? "true" : undefined,
            "aria-describedby": errorId || undefined,
          });
        }
        return child;
      })}
      <ErrorMessage error={error} id={errorId} />
    </div>
  );
};

/**
 * Get option value and label from option object or string
 * For Liferay DDM forms, label can be an object with language keys like { "en_US": "Label" }
 */
const getOptionData = (option, index) => {
  if (typeof option === "string") {
    return { value: option, label: option };
  }

  // Extract label - handle both string and object format { "en_US": "Label" }
  let label = option.label;
  if (label && typeof label === "object") {
    // Try to get English label first, then any available language
    label = label.en_US || label.en || Object.values(label)[0] || "";
  }
  label =
    label ||
    option.name ||
    option.reference ||
    option.value ||
    option.id ||
    String(index);

  // For form submission, we want to save the label text, not the option value
  // So return label as both value and label for internal use
  return {
    value: option.value || option.id || String(index), // Keep original value for comparison
    label: label, // Display label
    reference: option.reference, // Keep reference if available
    optionValue: option.value, // Keep original option value for internal matching
  };
};

/**
 * Render select/dropdown field
 * Saves label text instead of option value (e.g., "Graduate" instead of "Option11709969")
 */
const SelectField = ({
  field,
  fieldId,
  fieldName,
  value,
  onChange,
  error,
  disabled,
  placeholder,
  required,
  label,
}) => {
  const options = field.options || field.values || [];

  // Handle change - extract label text from selected option
  const handleChange = (e) => {
    const selectedValue = e.target.value;

    // If selected value matches an option value, use the label text instead
    const selectedOption = options.find((opt, idx) => {
      const optData = getOptionData(opt, idx);
      return (
        optData.optionValue === selectedValue || optData.value === selectedValue
      );
    });

    if (selectedOption) {
      const optData = getOptionData(
        selectedOption,
        options.indexOf(selectedOption),
      );
      // Save label text (e.g., "Graduate") instead of option value (e.g., "Option11709969")
      onChange({ target: { name: fieldName, value: optData.label } });
    } else {
      // Fallback to selected value if option not found
      onChange({ target: { name: fieldName, value: selectedValue } });
    }
  };

  // Find current option by comparing value with labels
  const currentOptionIndex = options.findIndex((opt, idx) => {
    const optData = getOptionData(opt, idx);
    return (
      optData.label === value ||
      optData.optionValue === value ||
      optData.value === value
    );
  });
  const currentValue =
    currentOptionIndex >= 0
      ? getOptionData(options[currentOptionIndex], currentOptionIndex)
        .optionValue ||
      getOptionData(options[currentOptionIndex], currentOptionIndex).value
      : value || "";

  return (
    <FormRow label={label} labelId={fieldId} required={required} error={error}>
      <select
        id={fieldId}
        name={fieldName}
        className={`form-input ${error ? "has-error" : ""}`}
        value={currentValue}
        onChange={handleChange}
        disabled={disabled}
      >
        <option value="">{placeholder || "Select an option"}</option>
        {options.map((option, index) => {
          const {
            value: optionValue,
            label: optionLabel,
            optionValue: optVal,
          } = getOptionData(option, index);
          return (
            <option key={optionValue || index} value={optVal || optionValue}>
              {optionLabel}
            </option>
          );
        })}
      </select>
    </FormRow>
  );
};

/**
 * Render textarea field
 */
const TextareaField = ({
  field,
  fieldId,
  fieldName,
  value,
  onChange,
  error,
  disabled,
  placeholder,
  validation,
  required,
  label,
}) => {
  const maxLen = isNameLikeSemanticField(field)
    ? NAME_FIELD_MAX_CHARS
    : validation.maxLength;
  return (
    <FormRow label={label} labelId={fieldId} required={required} error={error}>
      <textarea
        id={fieldId}
        name={fieldName}
        className={`form-input ${error ? "has-error" : ""}`}
        value={value || ""}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        rows={field.rows || 4}
        maxLength={maxLen}
      />
    </FormRow>
  );
};

/**
 * Render file upload field
 */
const FileField = ({
  field,
  fieldId,
  fieldName,
  value,
  onChange,
  error,
  disabled,
  required,
  label,
}) => (
  <FormRow label={label} labelId={fieldId} required={required} error={error} >
    <input
      id={fieldId}
      name={fieldName}
      type="file"
      className={`form-input ${error ? "has-error" : ""}`}
      onChange={onChange}
      disabled={disabled}
      accept={field.accept || "*"}
    />
    {value && typeof value === "object" && value.name && (
      <div className="file-name">{value.name}</div>
    )}
  </FormRow>
);

/**
 * Render checkbox field
 */
const CheckboxField = ({
  field: _field,
  fieldId,
  fieldName,
  value,
  onChange,
  error,
  disabled,
  required,
  label,
}) => (
  <FormRow label={null} labelId={fieldId} required={required} error={error}>
    <label className="form-label checkbox-label" htmlFor={fieldId}>
      <input
        id={fieldId}
        name={fieldName}
        type="checkbox"
        checked={value || false}
        onChange={(e) =>
          onChange({
            target: {
              name: fieldName,
              value: e.target.checked,
            },
          })
        }
        disabled={disabled}
      />
      <span>
        {label}
        {required && <span style={{ color: "#dc2626" }}> *</span>}
      </span>
    </label>
  </FormRow>
);

/**
 * Render radio field
 * Saves label text instead of option value (e.g., "Yes" instead of "Option94086906")
 */
const RadioField = ({
  field,
  fieldId: _fieldId,
  fieldName,
  value,
  onChange,
  error,
  disabled,
  required,
  label,
}) => {
  const options = field.options || field.values || [];

  // Handle change - extract label text from selected option
  // For radio fields, we handle selection differently by directly finding the option
  const handleChange = (e) => {
    const selectedValue = e.target.value;

    // Find the selected option by matching the radio value
    let selectedOptData = null;

    for (let idx = 0; idx < options.length; idx++) {
      const opt = options[idx];
      const optData = getOptionData(opt, idx);
      if (
        optData.optionValue === selectedValue ||
        optData.value === selectedValue
      ) {
        selectedOptData = optData;
        break;
      }
    }

    if (selectedOptData) {
      // Save label text (e.g., "Yes") instead of option value (e.g., "Option94086906")
      onChange({ target: { name: fieldName, value: selectedOptData.label } });
    } else {
      // Fallback to selected value if option not found
      onChange({ target: { name: fieldName, value: selectedValue } });
    }
  };

  return (
    <FormRow label={label} labelId={null} required={required} error={error}>
      <div className="radio-group">
        {options.map((option, index) => {
          const {
            value: optionValue,
            label: optionLabel,
            optionValue: optVal,
          } = getOptionData(option, index);
          const radioValue = optVal || optionValue;
          // Check if current value matches either the label or the option value
          const isChecked =
            value === optionLabel ||
            value === radioValue ||
            value === optionValue;
          return (
            <label
              key={optionValue || index}
              className={`radio-label ${isChecked ? "selected" : ""}`}
            >
              <input
                type="radio"
                name={fieldName}
                value={radioValue}
                checked={isChecked}
                onChange={handleChange}
                disabled={disabled}
              />
              <span>{optionLabel}</span>
            </label>
          );
        })}
      </div>
    </FormRow>
  );
};

/**
 * Render date field.
 * Do not use HTML min/max on type="date" — browsers show native tooltips that clash with our inline errors.
 * Range rules for DOB are enforced in dynamicValidation (validateDOBCustom).
 */
const DateField = ({
  fieldId,
  fieldName,
  value,
  onChange,
  error,
  disabled,
  required,
  label,
}) => {
  return (
    <FormRow label={label} labelId={fieldId} required={required} error={error}>
      <div className="input-wrapper">
        <input
          id={fieldId}
          name={fieldName}
          type="date"
          className={`form-input ${error ? "has-error" : ""}`}
          value={value || ""}
          onChange={onChange}
          onInvalid={(e) => {
            e.preventDefault();
          }}
          disabled={disabled}
        />
      </div>
    </FormRow>
  );
};

const eightDigitsToIso = (digits8) => {
  if (!digits8 || digits8.length !== 8) return "";
  const dd = digits8.slice(0, 2);
  const mm = digits8.slice(2, 4);
  const yyyy = digits8.slice(4, 8);
  const d = Number(dd);
  const mo = Number(mm);
  const y = Number(yyyy);
  if (!Number.isFinite(d) || !Number.isFinite(mo) || !Number.isFinite(y)) return "";
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return "";
  const dt = new Date(y, mo - 1, d);
  if (
    dt.getFullYear() !== y ||
    dt.getMonth() !== mo - 1 ||
    dt.getDate() !== d
  ) {
    return "";
  }
  return `${yyyy}-${mm}-${dd}`;
};

const formatDdMmYyyyDigits = (digits) => {
  if (digits.length <= 2) return digits;
  if (digits.length <= 4) return `${digits.slice(0, 2)}/${digits.slice(2)}`;
  return `${digits.slice(0, 2)}/${digits.slice(2, 4)}/${digits.slice(4)}`;
};

/** Auto-insert slashes while typing; ignore calendar clicks so picker selection still works. */
const handleDobRawInputChange = (e, fieldName, onFieldChange) => {
  if (e.nativeEvent instanceof MouseEvent) return;

  const digits = e.target.value.replace(/\D/g, "").slice(0, 8);
  e.target.value = formatDdMmYyyyDigits(digits);

  if (digits.length === 8) {
    const iso = eightDigitsToIso(digits);
    if (iso) {
      onFieldChange({
        target: { name: fieldName, value: iso },
      });
    }
  } else if (digits.length === 0) {
    onFieldChange({
      target: { name: fieldName, value: "" },
    });
  }
};

const today = new Date();

const maxDateLead = new Date(
  today.getFullYear() - 18,
  today.getMonth(),
  today.getDate()
);

const minDateLead = new Date(
  today.getFullYear() - 60,
  today.getMonth() - 11,
  today.getDate() - 29
);

const localDateFromYyyyMmDd = (raw) => {
  const s = String(raw || "").trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return null;
  const y = Number(m[1]);
  const mo = Number(m[2]) - 1;
  const d = Number(m[3]);
  if (!Number.isFinite(y) || !Number.isFinite(mo) || !Number.isFinite(d)) return null;
  const dt = new Date(y, mo, d);
  if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
  return dt;
};

const localDateToYyyyMmDd = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "";
  const y = date.getFullYear();
  const mo = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${mo}-${d}`;
};

const DdMmYyyyDobField = ({
  fieldId,
  fieldName,
  value,
  onChange,
  error,
  disabled,
  required,
  label,
}) => {
  const selected = localDateFromYyyyMmDd(value);

  return (
    <FormRow label={label} labelId={fieldId} required={required} error={error}>
      <div
        className={`input-wrapper dob-datepicker-wrapper${error ? " has-error" : ""}`}
      >
        <DatePicker
          id={fieldId}
          selected={selected}
          onChange={(date) => {
            onChange({
              target: {
                name: fieldName,
                value: date ? localDateToYyyyMmDd(date) : "",
              },
            });
          }}
          onChangeRaw={(e) => handleDobRawInputChange(e, fieldName, onChange)}
          dateFormat="dd/MM/yyyy"
          placeholderText="DD/MM/YYYY"
          minDate={minDateLead}
          maxDate={maxDateLead}
          showMonthDropdown
          showYearDropdown
          dropdownMode="select"
          scrollableYearDropdown
          yearDropdownItemNumber={100}
          showIcon
          toggleCalendarOnIconClick
          openOnFocus
          disabled={disabled}
          autoComplete="off"
          className={`form-input ${error ? "has-error" : ""}`}
          wrapperClassName="dob-datepicker-inner"
          popperPlacement="bottom-start"
        />
      </div>
    </FormRow>
  );
};

/**
 * Render mobile field with prefix
 */
const MobileField = ({
  field: _field,
  fieldId,
  fieldName,
  value,
  onChange,
  error,
  disabled,
  placeholder,
  validation: _validation,
  required,
  label,
  inputRef: _inputRef,
}) => {
  // Always use 10 digits for mobile number (custom validation, ignore form builder)
  const maxLength = 10;

  // Ensure value is always a string
  const stringValue = String(value || "");

  // Handle input change with proper digit filtering and length limiting
  const handleInputChange = (e) => {
    const inputValue = e.target.value;
    // Remove all non-digit characters
    const digitsOnly = inputValue.replace(/\D/g, "");
    // 1st digit should be 6 or above
    if (digitsOnly.length > 0 && !/^[6-9]/.test(digitsOnly)) {
      return;
    }
    // Limit to exactly 10 digits
    const limitedValue = digitsOnly.slice(0, maxLength);

    // Always pass as string to prevent decimal conversion
    onChange({
      target: {
        name: fieldName,
        value: limitedValue, // Keep as string, never convert to number
      },
    });
  };

  return (
    <FormRow label={label} labelId={fieldId} required={required} error={error}>
      <div className="input-wrapper mobile-input-wrapper">
        <div className="input-prefix">
          <svg
            className="flag-icon"
            width="20"
            height="15"
            viewBox="0 0 20 15"
            fill="none"
          >
            <rect width="20" height="15" fill="#FF9933" />
            <rect y="5" width="20" height="5" fill="#FFFFFF" />
            <rect y="10" width="20" height="5" fill="#138808" />
            {/* Ashoka Chakra: 24-spoke navy wheel on the white stripe */}
            <circle cx="10" cy="7.5" r="2.3" fill="none" stroke="#0000AB" strokeWidth="0.15" />
            <circle cx="10" cy="7.5" r="0.3" fill="#0000AB" />
            <line x1="10.000" y1="7.150" x2="10.000" y2="5.200" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.091" y1="7.162" x2="10.595" y2="5.278" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.175" y1="7.197" x2="11.150" y2="5.508" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.247" y1="7.253" x2="11.626" y2="5.874" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.303" y1="7.325" x2="11.992" y2="6.350" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.338" y1="7.409" x2="12.222" y2="6.905" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.350" y1="7.500" x2="12.300" y2="7.500" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.338" y1="7.591" x2="12.222" y2="8.095" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.303" y1="7.675" x2="11.992" y2="8.650" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.247" y1="7.747" x2="11.626" y2="9.126" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.175" y1="7.803" x2="11.150" y2="9.492" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.091" y1="7.838" x2="10.595" y2="9.722" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="10.000" y1="7.850" x2="10.000" y2="9.800" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.909" y1="7.838" x2="9.405" y2="9.722" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.825" y1="7.803" x2="8.850" y2="9.492" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.753" y1="7.747" x2="8.374" y2="9.126" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.697" y1="7.675" x2="8.008" y2="8.650" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.662" y1="7.591" x2="7.778" y2="8.095" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.650" y1="7.500" x2="7.700" y2="7.500" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.662" y1="7.409" x2="7.778" y2="6.905" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.697" y1="7.325" x2="8.008" y2="6.350" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.753" y1="7.253" x2="8.374" y2="5.874" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.825" y1="7.197" x2="8.850" y2="5.508" stroke="#0000AB" strokeWidth="0.15" />
            <line x1="9.909" y1="7.162" x2="9.405" y2="5.278" stroke="#0000AB" strokeWidth="0.15" />
          </svg>
          <span className="prefix-text">+91</span>
        </div>
        <input
          id={fieldId}
          name={fieldName}
          type="text"
          className={`form-input ${error ? "has-error" : ""}`}
          value={stringValue}
          onChange={handleInputChange}
          placeholder={placeholder || "e.g. 98762 72383"}
          disabled={disabled}
          maxLength={maxLength}
          pattern="[0-9]*"
          inputMode="numeric"
          autoComplete="off"
        />
      </div>
    </FormRow>
  );
};

/**
 * Render income field with currency symbol
 */
const IncomeField = ({
  field,
  fieldId,
  fieldName,
  value,
  onChange,
  error,
  disabled,
  placeholder,
  validation,
  required,
  label,
  inputRef
}) => (
  <FormRow label={label} labelId={fieldId} required={required} error={error}>
    <div className="input-wrapper currency-input-wrapper">
      <span className="currency-symbol">₹</span>
      <input
       ref={inputRef}
        id={fieldId}
        name={fieldName}
        type="text"
        className={`form-input ${error ? "has-error" : ""}`}
        value={value || ""}
        onChange={(e) =>
          onChange({
            target: {
              name: fieldName,
              value: e.target.value.replace(/\D/g, ""),
            },
          })
        }
        placeholder={placeholder || "e.g. 50,000 per year"}
        disabled={disabled}
        maxLength={validation.maxLength}
        min={validation.min}
        max={validation.max}
        step={field.step}
        pattern={validation.pattern}
        autoComplete="off"
      />
    </div>
  </FormRow>
);

/** API may return `pincode` or `pinCode` on suggestion objects */
const getSuggestionPincode = (suggestion) => {
  if (!suggestion) return "";
  const v = suggestion.pincode ?? suggestion.pinCode;
  return v != null ? String(v) : "";
};

/** Block typed non-digits; allows Backspace, arrows, Tab, shortcuts (paste still sanitized in onChange). */
const shouldBlockPincodeKey = (e) => {
  if (e.ctrlKey || e.metaKey || e.altKey) return false;
  if (e.key.length !== 1) return false;
  return !/[0-9]/.test(e.key);
};

/**
 * Render pincode field with autocomplete suggestions
 */
const PincodeField = ({
  field: _field,
  fieldId,
  fieldName,
  value,
  onChange,
  onPincodeSuggestionsChange,
  error,
  disabled,
  placeholder,
  validation,
  required,
  label,
  inputRef
}) => {
  const notifyPincodeSuggestions = (resultCount) => {
    onPincodeSuggestionsChange?.({ resultCount });
  };
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value || "");
  const wrapperRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const selectedSuggestionRef = useRef(null); // Store selected suggestion for display format
  // Tracks the last pincode we already resolved to a state, so we don't re-emit state
  // onChange events on every keystroke / re-render once it's set.
  const lastResolvedStatePincodeRef = useRef(null);

  // Emits communicationState / branchState / residencyState onChange events from a pincode-master
  // record. Centralises the autocomplete-select and typed-pincode paths so they stay in sync.
  const emitStateFieldsFromRecord = (record) => {
    const resolvedState =
      record?.state ||
      record?.stateName ||
      record?.stateUT ||
      record?.stateUt;
    const resolvedCity = record?.city ?? record?.cityName ?? record?.cityDistrict;
    if (resolvedState) {
      onChange({ target: { name: "communicationState", value: resolvedState } });
      onChange({ target: { name: "branchState", value: resolvedState } });
      onChange({ target: { name: "residencyState", value: resolvedState } });
    }
    if (resolvedCity) {
      onChange({ target: { name: "city", value: resolvedCity } });
      onChange({ target: { name: "residencyCity", value: resolvedCity } });
      onChange({ target: { name: "communicationCity", value: resolvedCity } });
    }
  };

  const clearLocationFieldsFromPin = () => {
    onChange({ target: { name: "communicationState", value: "" } });
    onChange({ target: { name: "branchState", value: "" } });
    onChange({ target: { name: "residencyState", value: "" } });
    onChange({ target: { name: "city", value: "" } });
    onChange({ target: { name: "residencyCity", value: "" } });
    onChange({ target: { name: "communicationCity", value: "" } });
    onChange({ target: { name: "cityAndPincode", value: "" } });
    lastResolvedStatePincodeRef.current = null;
  };

  // Keep local input in sync with form value; always show digits-only so the field stays editable.
  useEffect(() => {
    if (!value || value === "") {
      setInputValue("");
      selectedSuggestionRef.current = null;
      return;
    }

    const digits = String(value).replace(/\D/g, "").slice(0, validation?.maxLength || 6);
    setInputValue(digits);
    if (selectedSuggestionRef.current) {
      const selectedPin = getSuggestionPincode(selectedSuggestionRef.current);
      if (String(selectedPin) !== digits) {
        selectedSuggestionRef.current = null;
      }
    }
  }, [value, validation?.maxLength]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (wrapperRef.current?.contains(event.target)) {
        return;
      }

      setShowSuggestions(false);
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Fetch suggestions when input has 3+ characters
  const fetchSuggestions = async (query) => {
    if (query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      notifyPincodeSuggestions(null);
      return;
    }

    setLoading(true);
    setShowSuggestions(false);

    try {
      const results = await fetchPincodeSuggestions(query);
      const resultCount = results?.length ?? 0;
      notifyPincodeSuggestions(resultCount);
      if (resultCount > 0) {
        setSuggestions(results);
        setShowSuggestions(true);
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (err) {
      notifyPincodeSuggestions(0);
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  };

  // Handle input change with debounce
  const handleInputChange = (e) => {
    const inputVal = e.target.value;

    const pincodePart = inputVal.includes(" / ")
      ? inputVal.split(" / ")[0].replace(/\D/g, "")
      : inputVal.replace(/\D/g, "");
    const trimmedValue = pincodePart.slice(0, validation?.maxLength || 6);

    selectedSuggestionRef.current = null;
    setInputValue(trimmedValue);
    onChange({ target: { name: fieldName, value: trimmedValue } });

    if (!trimmedValue) {
      clearLocationFieldsFromPin();
    } else if (trimmedValue.length < 6) {
      lastResolvedStatePincodeRef.current = null;
      clearLocationFieldsFromPin();
    }

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (trimmedValue.length >= 3) {
      debounceTimerRef.current = setTimeout(
        () => fetchSuggestions(trimmedValue),
        300,
      );
    } else {
      setSuggestions([]);
      setShowSuggestions(false);
      notifyPincodeSuggestions(null);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = (suggestion) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = null;
    }

    // Store selected suggestion to maintain display format
    selectedSuggestionRef.current = suggestion;

    const pin = getSuggestionPincode(suggestion);
    setInputValue(pin);
    setShowSuggestions(false);
    setSuggestions([]);
    setLoading(false);
    notifyPincodeSuggestions(1);

    // Update pincode field
    onChange({ target: { name: fieldName, value: pin } });

    // Update cityAndPincode field - backend expects exact field name "cityAndPincode"
    const cityAndPincodeValue = `${pin} / ${suggestion.city ?? ""}`;
    onChange({
      target: { name: "cityAndPincode", value: cityAndPincodeValue },
    });

    emitStateFieldsFromRecord(suggestion);
    lastResolvedStatePincodeRef.current = pin;
  };

  // Auto-resolve state when a 6-digit pincode is set without an autocomplete
  // selection — e.g. user typed the digits and tabbed away, or the form was
  // rehydrated from a saved lead. Without this, communicationState /
  // branchState / residencyState stay blank and the backend ends up sending
  // empty @LI_STATE / @CUSCOM to the BI API even for Indian residents.
  useEffect(() => {
    const digits = String(value ?? "").replace(/\D/g, "");
    if (digits.length !== 6) {
      if (digits.length === 0) {
        lastResolvedStatePincodeRef.current = null;
      }
      return;
    }
    if (lastResolvedStatePincodeRef.current === digits) {
      return;
    }

    let cancelled = false;
    getPincodeMasterRecord(digits)
      .then((record) => {
        if (cancelled || !record) return;
        emitStateFieldsFromRecord(record);
        lastResolvedStatePincodeRef.current = digits;
      })
      .catch(() => {
        // Silent: pincode master lookup is best-effort. If it fails (offline,
        // unmapped pincode), backend still has the pincode and can fall back.
      });

    return () => {
      cancelled = true;
    };
    // emitStateFieldsFromRecord closes over onChange but is stable enough for
    // this effect; we intentionally only re-run when the pincode value changes.
  }, [value]);

  return (
    <FormRow label={label} labelId={fieldId} required={required} error={error}>
      <div className="input-wrapper pincode-input-wrapper" ref={wrapperRef}>
        <div className="input-box">
          <span class="pin-loc">
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M14.5 7H12.9769C12.8584 5.72007 12.296 4.52188 11.387 3.61296C10.4781 2.70404 9.27993 2.14163 8 2.02312V0.5C8 0.367392 7.94732 0.240215 7.85355 0.146447C7.75979 0.0526784 7.63261 0 7.5 0C7.36739 0 7.24021 0.0526784 7.14645 0.146447C7.05268 0.240215 7 0.367392 7 0.5V2.02312C5.72007 2.14163 4.52188 2.70404 3.61296 3.61296C2.70404 4.52188 2.14163 5.72007 2.02312 7H0.5C0.367392 7 0.240215 7.05268 0.146447 7.14645C0.0526784 7.24021 0 7.36739 0 7.5C0 7.63261 0.0526784 7.75979 0.146447 7.85355C0.240215 7.94732 0.367392 8 0.5 8H2.02312C2.14163 9.27993 2.70404 10.4781 3.61296 11.387C4.52188 12.296 5.72007 12.8584 7 12.9769V14.5C7 14.6326 7.05268 14.7598 7.14645 14.8536C7.24021 14.9473 7.36739 15 7.5 15C7.63261 15 7.75979 14.9473 7.85355 14.8536C7.94732 14.7598 8 14.6326 8 14.5V12.9769C9.27993 12.8584 10.4781 12.296 11.387 11.387C12.296 10.4781 12.8584 9.27993 12.9769 8H14.5C14.6326 8 14.7598 7.94732 14.8536 7.85355C14.9473 7.75979 15 7.63261 15 7.5C15 7.36739 14.9473 7.24021 14.8536 7.14645C14.7598 7.05268 14.6326 7 14.5 7ZM7.5 12C6.60998 12 5.73995 11.7361 4.99993 11.2416C4.25991 10.7471 3.68314 10.0443 3.34254 9.22208C3.00195 8.39981 2.91283 7.49501 3.08647 6.62209C3.2601 5.74918 3.68868 4.94736 4.31802 4.31802C4.94736 3.68868 5.74918 3.2601 6.62209 3.08647C7.49501 2.91283 8.39981 3.00195 9.22208 3.34254C10.0443 3.68314 10.7471 4.25991 11.2416 4.99993C11.7361 5.73995 12 6.60998 12 7.5C11.9987 8.69307 11.5241 9.83689 10.6805 10.6805C9.83689 11.5241 8.69307 11.9987 7.5 12ZM7.5 5C7.00555 5 6.5222 5.14662 6.11107 5.42133C5.69995 5.69603 5.37952 6.08648 5.1903 6.54329C5.00108 7.00011 4.95157 7.50277 5.04804 7.98773C5.1445 8.47268 5.3826 8.91814 5.73223 9.26777C6.08186 9.6174 6.52732 9.8555 7.01227 9.95196C7.49723 10.0484 7.99989 9.99892 8.45671 9.8097C8.91352 9.62048 9.30397 9.30005 9.57867 8.88893C9.85338 8.4778 10 7.99445 10 7.5C10 6.83696 9.73661 6.20107 9.26777 5.73223C8.79893 5.26339 8.16304 5 7.5 5ZM7.5 9C7.20333 9 6.91332 8.91203 6.66664 8.7472C6.41997 8.58238 6.22771 8.34811 6.11418 8.07403C6.00065 7.79994 5.97094 7.49834 6.02882 7.20736C6.0867 6.91639 6.22956 6.64912 6.43934 6.43934C6.64912 6.22956 6.91639 6.0867 7.20736 6.02882C7.49834 5.97094 7.79994 6.00065 8.07403 6.11418C8.34811 6.22771 8.58238 6.41997 8.7472 6.66664C8.91203 6.91332 9 7.20333 9 7.5C9 7.89782 8.84196 8.27936 8.56066 8.56066C8.27936 8.84196 7.89782 9 7.5 9Z"
                fill="#AE272C"
              ></path>
            </svg>
          </span>
          <input
            ref={inputRef}
            id={fieldId}
            name={fieldName}
            type="text"
            className={`form-input ${error ? "has-error" : ""}`}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={(e) => {
              if (disabled) return;
              if (shouldBlockPincodeKey(e)) e.preventDefault();
            }}
            onPaste={(e) => {
              if (disabled) return;
              e.preventDefault();
              const text = (e.clipboardData || window.clipboardData)?.getData(
                "text",
              );
              if (text == null) return;
              const digits = String(text)
                .replace(/\D/g, "")
                .slice(0, validation?.maxLength ?? 6);
              const synthetic = { target: { value: digits } };
              handleInputChange(synthetic);
            }}
            placeholder={placeholder || "e.g. 201301"}
            disabled={disabled}
            maxLength={
              String(inputValue).includes(" / ")
                ? undefined
                : validation?.maxLength ?? 6
            }
            inputMode="numeric"
            autoComplete="off"
          />
        </div>
        {loading && <span className="pincode-loading">Loading...</span>}
        {showSuggestions && suggestions.length > 0 && (
          <div className="pincode-suggestions">
            <div className="pincode-scrollbar">
              {suggestions.map((suggestion) => (
                <div
                  key={suggestion.id || getSuggestionPincode(suggestion)}
                  className={`pincode-suggestion-item ${inputValue === getSuggestionPincode(suggestion) ? "active" : ""}`}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleSuggestionSelect(suggestion);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleSuggestionSelect(suggestion);
                    }
                  }}
                  role="option"
                  tabIndex={0}
                  aria-selected={
                    inputValue ===
                    `${getSuggestionPincode(suggestion)} / ${suggestion.city}` ||
                    inputValue === getSuggestionPincode(suggestion)
                  }
                >
                  <span className="pin-text">
                    <span className="pin">{getSuggestionPincode(suggestion)}</span>{" "}
                    {suggestion.city}
                  </span>
                  <span className="pincode-suggestion-radio">
                    {inputValue ===
                      `${getSuggestionPincode(suggestion)} / ${suggestion.city}` ||
                      inputValue === getSuggestionPincode(suggestion) ? (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="8"
                          cy="8"
                          r="7.5"
                          fill="white"
                          stroke="#97262A"
                        />
                        <circle
                          cx="8"
                          cy="8"
                          r="3.5"
                          fill="#97262A"
                          stroke="#97262A"
                        />
                      </svg>
                    ) : (
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 16 16"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <circle
                          cx="8"
                          cy="8"
                          r="7.5"
                          fill="white"
                          stroke="#97262A"
                        />
                      </svg>
                    )}
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </FormRow>
  );
};

/**
 * Render default text input field
 */
const TextField = ({
  field,
  fieldId,
  fieldName,
  value,
  onChange,
  error,
  disabled,
  placeholder,
  validation,
  required,
  label,
  fieldType,
  fieldReference,
  inputRef
}) => {
  const effectiveMaxLength = isNameLikeSemanticField(field)
    ? NAME_FIELD_MAX_CHARS
    : validation.maxLength;

  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
   const itemRefs = useRef([]);

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    if (e.key === "ArrowDown") {
      e.preventDefault();

      setActiveIndex((prev) =>
        prev < suggestions.length - 1 ? prev + 1 : 0
      );
    }
    else if (e.key === "ArrowUp") {
      e.preventDefault();

      setActiveIndex((prev) =>
        prev > 0 ? prev - 1 : suggestions.length - 1
      );
    }

    else if (e.key === "PageDown") {
      e.preventDefault();

      setActiveIndex((prev) =>
        Math.min(prev + 1, suggestions.length - 1)
      );
    }

    else if (e.key === "PageUp") {
      e.preventDefault();

      setActiveIndex((prev) =>
        Math.max(prev - 1, 0)
      );
    }

    else if (e.key === "Enter") {
      if (activeIndex >= 0) {
        e.preventDefault();
        handleSelect(suggestions[activeIndex]);
      }
    }
  };

  useEffect(() => {
    if (activeIndex >= 0) {
      itemRefs.current[activeIndex]?.scrollIntoView({
        block: "nearest",
        behavior: "smooth",
      });
    }
  }, [activeIndex]);


  const handleEmailChange = (e) => {
    const val = e.target.value;
    onChange(e);

    if (fieldReference === "emailAddress") {
      const atIndex = val.indexOf("@");
      if (val.length > 50) return; 
      if (atIndex !== -1) {
        const typedDomain = val.slice(atIndex + 1).toLowerCase();

        const filtered = EMAIL_DOMAINS.filter((domain) =>
          domain.startsWith(typedDomain)
        );

        setSuggestions(filtered);
        setShowSuggestions(true);
      } else {
        setShowSuggestions(false);
      }
    }
  };

  const handleSelect = (domain) => {
    const namePart = (value || "").split("@")[0];
    const newEmail = `${namePart}@${domain}`;
    if (newEmail.length > 50) {
      return;
    }
    onChange({
      target: { name: fieldName, value: newEmail },
    });

    setShowSuggestions(false);
  };

    const wrapperRef = useRef(null);
     useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setSuggestions([]);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  return (
    <FormRow label={label} labelId={fieldId} required={required} error={error}>

      {fieldReference === "emailAddress" ? (
        <div ref={wrapperRef} className="proposal-autocomplete-wrapper">
          <input
          ref={inputRef}
            id={fieldId}
            name="random_email_field"
            type="text"
            className={`form-input ${error ? "has-error" : ""}`}
            value={value || ""}
            onChange={handleEmailChange}
            placeholder={placeholder}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            maxLength={50}
            min={validation.min}
            max={validation.max}
            autoComplete="off"
            step={field.step}
            pattern={validation.pattern}
          />

          {fieldReference === "emailAddress" && showSuggestions && suggestions.length > 0 && (
            <div className="proposal-autocomplete-dropdown">
               {suggestions.map((domain, index) => (
                <div
                  key={domain}
                  ref={(el) => (itemRefs.current[index] = el)}
                  className={`proposal-autocomplete-option ${activeIndex === index ? "active" : ""
                    }`}
                  onMouseDown={() => handleSelect(domain)}
                >
                  {(value || "").split("@")[0]}@{domain}
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <input
        ref={inputRef}
          id={fieldId}
          name="no-autofill-address"
          type={fieldType}
          className={`form-input ${error ? "has-error" : ""}`}
          value={value || ""}
          onChange={onChange}
          placeholder={placeholder}
          disabled={disabled}
          maxLength={effectiveMaxLength}
          min={validation.min}
          max={validation.max}
          autoComplete="off"
          step={field.step}
          pattern={validation.pattern}
        />
      )}
    </FormRow>
  );
};

const getLocalizedValue = (obj, fallback = "") => {
  if (!obj) return fallback;
  if (typeof obj === "string") return obj;
  return obj.en_US || obj[Object.keys(obj)[0]] || fallback;
};

const extractFieldMetadata = (field) => {
  // Extract field name consistently - prefer fieldReference (semantic name) over DDM field name
  // fieldReference is stable and doesn't change when form is recreated, unlike DDM field names
  const fieldName = field.fieldReference || field.name || field.fieldName;
  return {
    fieldName,
    fieldId: field.id || fieldName,
    fieldType: field.type || "text",
    label: getLocalizedValue(field.label || field.title, fieldName),
    placeholder: getLocalizedValue(field.placeholder),
    required: field.validation?.required ?? field.required ?? false,
    validation: field.validation || {},
    fieldReference: field.fieldReference || "",
  };
};

/**
 * Detect special field types based on field name/type
 * Checks both field.name and field.fieldReference for better detection
 */
const detectSpecialFieldType = (fieldType, field, fieldName) => {
  // Check both name and fieldReference for detection
  const nameToCheck =
    fieldName || field?.name || field?.fieldName || field?.fieldReference || "";
  const fieldReferenceToCheck = field?.fieldReference || "";
  const combinedName = `${nameToCheck} ${fieldReferenceToCheck}`
    .toLowerCase()
    .trim();

  const mobileTypes = ["tel", "phone", "mobile"];
  const mobileKeywords = ["mobile", "phone", "mobilenumber", "mobile_number"];
  const incomeKeywords = ["income", "salary", "annual"];
  // More comprehensive pincode keywords to catch various naming conventions
  const pincodeKeywords = [
    "pincode",
    "pin_code",
    "pin",
    "postal",
    "postalcode",
    "postal_code",
    "zip",
    "zipcode",
    "zip_code",
    "pincod",
    "pinc",
    "postalcod",
    "postcode",
  ];

  const isMobileField =
    mobileTypes.includes(fieldType) ||
    mobileKeywords.some((keyword) => combinedName.includes(keyword));
  const isIncomeField = incomeKeywords.some((keyword) =>
    combinedName.includes(keyword),
  );
  const isPincodeField = pincodeKeywords.some((keyword) =>
    combinedName.includes(keyword),
  );

  return { isMobileField, isIncomeField, isPincodeField };
};

const transformInputValue = (value, fieldType, validation) => {
  if (["tel", "phone", "mobile"].includes(fieldType)) {
    const digits = value.replace(/\D/g, "");
    const maxLen = validation.maxLength || validation.exactLength;
    return maxLen ? digits.slice(0, maxLen) : digits;
  }
  if (fieldType === "number") {
    if (value === "") return "";
    const num = parseFloat(value);
    return !isNaN(num) ? num.toString() : null;
  }
  return value;
};

const createTextChangeHandler =
  (fieldName, fieldType, validation, onChange, field) => (e) => {
    let newValue = transformInputValue(e.target.value, fieldType, validation);
    if (newValue === null) return;
    if (typeof newValue === "string" && field && isNameLikeSemanticField(field)) {
      newValue = newValue.slice(0, NAME_FIELD_MAX_CHARS);
    }
    onChange({ target: { name: fieldName, value: newValue } });
  };

// SelectField now handles its own change logic to save label text instead of option value
const createSelectChangeHandler = (fieldName, onChange) => (e) =>
  onChange({ target: { name: fieldName, value: e.target.value } });

const createFileChangeHandler = (fieldName, onChange) => (e) =>
  onChange({ target: { name: fieldName, value: e.target.files?.[0] || null } });

const typeMap = {
  select: SelectField,
  dropdown: SelectField,
  textarea: TextareaField,
  file: FileField,
  fileupload: FileField,
  checkbox: CheckboxField,
  radio: RadioField,
  date: DateField,
};

const DynamicFormFieldInner = ({
  field,
  value,
  onChange,
  onPincodeSuggestionsChange,
  error,
  disabled = false,
  /** Lead basic step 0: show DOB as DD/MM/YYYY while keeping YYYY-MM-DD in form state */
  useDdMmYyyyForDob = false,
  autoFocus = false
}) => {
  const {
    fieldName,
    fieldId,
    fieldType,
    fieldReference,
    label,
    placeholder,
    required,
    validation,
  } = extractFieldMetadata(field);
  const inputRef = useRef(null);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);
  const { isMobileField, isIncomeField, isPincodeField } =
    detectSpecialFieldType(fieldType, field, fieldName);
  const fieldTypeLower = fieldType.toLowerCase();

  const handlers = {
    text: createTextChangeHandler(
      fieldName,
      fieldType,
      validation,
      onChange,
      field,
    ),
    select: createSelectChangeHandler(fieldName, onChange),
    file: createFileChangeHandler(fieldName, onChange),
    onChange,
  };

  const commonProps = {
    field,
    fieldId,
    fieldName,
    value,
    error,
    disabled,
    placeholder,
    validation,
    required,
    label,
    inputRef,
  };

  const FieldComponent = typeMap[fieldTypeLower];
  const useDmyDob =
    Boolean(useDdMmYyyyForDob) &&
    FieldComponent === DateField &&
    isDateOfBirthField(field);

  if (FieldComponent) {
    // DateField uses onChange directly (not text handler) to preserve date format
    // Extract nested ternary into independent statement for better readability
    let handler = handlers.text;
    if (FieldComponent === SelectField || FieldComponent === TextareaField) {
      handler = handlers.select;
    } else if (FieldComponent === FileField) {
      handler = handlers.file;
    } else if (
      FieldComponent === CheckboxField ||
      FieldComponent === DateField
    ) {
      handler = handlers.onChange;
    }
    if (useDmyDob) {
      return (
        <DdMmYyyyDobField {...commonProps} onChange={handlers.onChange} />
      );
    }
    return <FieldComponent {...commonProps} onChange={handler} />;
  }

  // Mobile field uses its own handler to prevent value conversion and ensure proper limiting
  if (isMobileField)
    return <MobileField {...commonProps} onChange={handlers.onChange} />;
  if (isIncomeField)
    return <IncomeField {...commonProps} onChange={handlers.text} />;
  if (isPincodeField)
    return (
      <PincodeField
        {...commonProps}
        onChange={handlers.onChange}
        onPincodeSuggestionsChange={onPincodeSuggestionsChange}
      />
    );
  return (
    <TextField
      {...commonProps}
      onChange={handlers.text}
      fieldType={fieldType}
      fieldReference={fieldReference}
    />
  );
};

const DynamicFormField = React.memo(DynamicFormFieldInner);

export default DynamicFormField;
